import {
  Card,
  CardContent,
  Button,
  Avatar,
  AvatarFallback,
} from "@b2b/ui-components";
import {
  MessageSquare,
  Globe,
  Image as ImageIcon,
  FileText,
  Award,
  Factory,
  PlayCircle,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { CompanyOverview } from "./CompanyOverview";
import { CompanyVideoPlayer } from "./CompanyVideoPlayer";
import { CustomerTestimonials } from "./CustomerTestimonials";
import { buildBusinessProfileSections } from "../utils/businessProfileDisplay";

interface CompanyProfileSectionProps {
  company?: Record<string, unknown>;
  storefront?: Record<string, unknown>;
  testimonials?: unknown[];
  seller?: Record<string, unknown>;
  businessInfo?: Record<string, unknown>;
  certifications?: unknown[];
  resolveIndustryLabel?: (value: string) => string;
  userRole?: "admin" | "seller" | "buyer";
  showChatForAuthenticated?: boolean;
}

const SectionHeading = ({
  icon: Icon,
  title,
}: {
  icon: typeof Globe;
  title: string;
}) => (
  <div className="mb-4 flex items-center gap-2">
    <Icon className="h-5 w-5 text-[#FF6A00]" />
    <h2 className="text-lg font-bold text-[#1A2A3D] sm:text-xl">{title}</h2>
  </div>
);

export const CompanyProfileSection = ({
  company,
  storefront,
  testimonials = [],
  seller,
  businessInfo,
  certifications,
  resolveIndustryLabel,
  userRole,
  showChatForAuthenticated = false,
}: CompanyProfileSectionProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const companyNameDisplay =
    typeof company?.name === "string" && company.name.trim() ? company.name : "Company";
  const { highlights } = buildBusinessProfileSections({
    company,
    businessInfo,
    companyDescription: storefront?.companyDescription as string | undefined,
    resolveIndustryLabel,
    seller,
  });

  const extended = (company?.businessProfileExtended as Record<string, unknown>) || {};
  const gallery = Array.isArray(storefront?.gallery)
    ? (storefront.gallery as Array<{ imageUrl?: string; caption?: string }>)
    : [];
  const brochures: string[] = Array.isArray(extended.companyBrochures)
    ? (extended.companyBrochures as unknown[]).filter(
        (url): url is string => typeof url === "string" && url.trim().length > 0,
      )
    : [];
  const ownBrands = Array.isArray(extended.ownBrands)
    ? (extended.ownBrands as Array<{ title?: string; fileUrl?: string }>)
    : [];
  const otherBrands = Array.isArray(extended.otherBrands)
    ? (extended.otherBrands as Array<{ title?: string; fileUrl?: string }>)
    : [];
  const awards = Array.isArray(extended.awardsRecognition)
    ? (extended.awardsRecognition as Array<{ title?: string; year?: string; fileUrl?: string }>)
    : [];
  const infrastructureImages: string[] = Array.isArray(extended.infrastructureImages)
    ? (extended.infrastructureImages as unknown[]).filter(
        (url): url is string => typeof url === "string" && url.trim().length > 0,
      )
    : [];
  const warehouseCertificates: string[] = Array.isArray(extended.warehouseCertificates)
    ? (extended.warehouseCertificates as unknown[]).filter(
        (url): url is string => typeof url === "string" && url.trim().length > 0,
      )
    : [];
  const annualCapacity = Array.isArray(extended.annualProductionCapacity)
    ? (extended.annualProductionCapacity as Array<{
        productName?: string;
        value?: number;
        unit?: string;
      }>)
    : [];

  const companyVideoUrl = (() => {
    const fromCompany =
      typeof company?.companyVideoUrl === "string"
        ? company.companyVideoUrl.trim()
        : "";
    if (fromCompany) return fromCompany;
    const fromYoutube =
      typeof company?.youtubeUrl === "string" ? company.youtubeUrl.trim() : "";
    return fromYoutube;
  })();

  const showMessageButton = showChatForAuthenticated || userRole === "buyer";
  const sellerName = seller
    ? `${seller.firstName || ""} ${seller.lastName || ""}`.trim() || "Sales Representative"
    : "Sales Representative";
  const initials = sellerName
    .split(" ")
    .map((part: string) => part[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
  const sellerId = typeof seller?.id === "string" && seller.id.trim() ? seller.id : "";

  const handleSendMessage = () => {
    const from = `${location.pathname}${location.search}`;
    if (sellerId) {
      navigate(`/chat?sellerId=${encodeURIComponent(sellerId)}`, { state: { from } });
      return;
    }
    navigate("/chat", { state: { from } });
  };

  const galleryWithImages = gallery.filter((item) => item.imageUrl);

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_280px]">
      <div className="min-w-0 space-y-8">
        <CompanyOverview
          company={company}
          businessInfo={businessInfo || (storefront?.businessInfo as Record<string, unknown>)}
          companyDescription={storefront?.companyDescription as string}
          certifications={certifications}
          resolveIndustryLabel={resolveIndustryLabel}
          seller={seller}
        />

        {companyVideoUrl ? (
          <section>
            <SectionHeading icon={PlayCircle} title="Company Video" />
            <div className="overflow-hidden rounded-xl border border-gray-100 bg-black/5">
              <CompanyVideoPlayer url={companyVideoUrl} />
            </div>
          </section>
        ) : null}

        {galleryWithImages.length > 0 ? (
          <section>
            <SectionHeading icon={ImageIcon} title="Gallery" />
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {galleryWithImages.map((item, index) => (
                <figure
                  key={`gallery-${index}`}
                  className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50"
                >
                  <img
                    src={item.imageUrl}
                    alt={item.caption || `Gallery image ${index + 1}`}
                    className="aspect-[4/3] w-full object-cover"
                  />
                  {item.caption ? (
                    <figcaption className="px-3 py-2 text-xs text-gray-600">
                      {item.caption}
                    </figcaption>
                  ) : null}
                </figure>
              ))}
            </div>
          </section>
        ) : null}

        {brochures.length > 0 ? (
          <section>
            <SectionHeading icon={FileText} title="Company Brochures" />
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {brochures.map((url, index) => (
                <li key={`brochure-${index}`}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-xl border border-gray-100 bg-white p-3 text-sm text-[#FF6A00] transition-colors hover:bg-orange-50"
                  >
                    <FileText className="h-4 w-4 shrink-0" />
                    <span className="truncate">Brochure {index + 1}</span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {ownBrands.length > 0 || otherBrands.length > 0 ? (
          <section>
            <SectionHeading icon={Award} title="Brands" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {ownBrands.length > 0 ? (
                <div className="rounded-xl border border-gray-100 bg-white p-4 sm:p-5">
                  <h3 className="mb-3 text-sm font-semibold text-gray-900">Own Brands</h3>
                  <ul className="space-y-2">
                    {ownBrands.map((brand, index) => (
                      <li key={`own-${index}`}>
                        {brand.fileUrl ? (
                          <a
                            href={brand.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#FF6A00] hover:underline"
                          >
                            {brand.title || `Brand ${index + 1}`}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-800">
                            {brand.title || `Brand ${index + 1}`}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {otherBrands.length > 0 ? (
                <div className="rounded-xl border border-gray-100 bg-white p-4 sm:p-5">
                  <h3 className="mb-3 text-sm font-semibold text-gray-900">Other Brands</h3>
                  <ul className="space-y-2">
                    {otherBrands.map((brand, index) => (
                      <li key={`other-${index}`}>
                        {brand.fileUrl ? (
                          <a
                            href={brand.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#FF6A00] hover:underline"
                          >
                            {brand.title || `Brand ${index + 1}`}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-800">
                            {brand.title || `Brand ${index + 1}`}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        {awards.length > 0 ? (
          <section>
            <SectionHeading icon={Award} title="Awards & Recognition" />
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {awards.map((award, index) => (
                <li key={`award-${index}`}>
                  <div className="h-full rounded-xl border border-gray-100 bg-white p-4">
                    <p className="font-semibold text-gray-900">{award.title || "Award"}</p>
                    {award.year ? (
                      <p className="mt-1 text-sm text-gray-500">{award.year}</p>
                    ) : null}
                    {award.fileUrl ? (
                      <a
                        href={award.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-block text-sm text-[#FF6A00] hover:underline"
                      >
                        View certificate
                      </a>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {infrastructureImages.length > 0 ? (
          <section>
            <SectionHeading icon={Factory} title="Infrastructure Image" />
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {infrastructureImages.map((url, index) => (
                <img
                  key={`infra-${index}`}
                  src={url}
                  alt={`Infrastructure ${index + 1}`}
                  className="aspect-[4/3] w-full rounded-xl border border-gray-100 object-cover"
                />
              ))}
            </div>
          </section>
        ) : null}

        {warehouseCertificates.length > 0 ? (
          <section>
            <SectionHeading icon={FileText} title="Warehouse Certificates" />
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {warehouseCertificates.map((url, index) => (
                <li key={`warehouse-${index}`}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-xl border border-gray-100 bg-white p-3 text-sm text-[#FF6A00] hover:bg-orange-50"
                  >
                    <FileText className="h-4 w-4 shrink-0" />
                    Certificate {index + 1}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {annualCapacity.length > 0 ? (
          <section>
            <SectionHeading icon={Factory} title="Annual Production Capacity" />
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Product</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Capacity</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {annualCapacity.map((row, index) => (
                    <tr key={`capacity-${index}`} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-gray-900">{row.productName || "—"}</td>
                      <td className="px-4 py-3 text-gray-900">{row.value ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-900">{row.unit || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        {testimonials.length > 0 ? (
          <section>
            <SectionHeading icon={MessageSquare} title="Customer Reviews" />
            <CustomerTestimonials testimonials={testimonials} />
          </section>
        ) : null}
      </div>

      <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
        {highlights.length > 0 ? (
          <Card className="border border-gray-100 shadow-sm">
            <CardContent className="space-y-3 p-4 sm:p-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                At a Glance
              </h3>
              <dl className="space-y-3">
                {highlights.slice(0, 6).map((item) => (
                  <div key={item.label}>
                    <dt className="text-xs text-gray-500">{item.label}</dt>
                    <dd className="text-sm font-medium text-gray-900">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        ) : null}

        <Card className="border border-gray-100 shadow-sm">
          <CardContent className="p-4 sm:p-5">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Contact Seller
            </h3>
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-[#FF6A00]/10 text-sm font-semibold text-[#FF6A00]">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[#1A2A3D]">{sellerName}</p>
                <p className="truncate text-xs text-gray-500">{companyNameDisplay}</p>
              </div>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-gray-500">
              Send a message through BizcoverIndia for secure communication with this supplier.
            </p>
            {showMessageButton ? (
              <Button
                className="mt-4 w-full"
                size="sm"
                variant="primary"
                onClick={handleSendMessage}
              >
                Send Message
              </Button>
            ) : null}
          </CardContent>
        </Card>
      </aside>
    </div>
  );
};
